import React, { useState, useEffect } from 'react';
import {
  Star,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Flag,
  CheckCircle,
  XCircle,
  MessageSquare,
  User,
  Calendar,
  Download,
  TrendingUp,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  MoreHorizontal
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../utils/api';

interface Review {
  id: number;
  reviewerId: number;
  reviewerName: string;
  reviewerType: 'client' | 'worker';
  revieweeId: number;
  revieweeName: string;
  rating: number;
  reviewText: string;
  responseText?: string;
  status: 'active' | 'hidden' | 'reported' | 'flagged';
  reportCount: number;
  reportReasons: string[];
  isVerified: boolean;
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
  moderationReason?: string;
}

interface ReviewsResponse {
  success: boolean;
  reviews: Review[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

const AddReviewForm: React.FC<{
  onSave: (reviewData: {
    reviewerId: number;
    revieweeId: number;
    reviewerType: 'client' | 'worker';
    revieweeType: 'client' | 'worker';
    rating: number;
    reviewText: string;
    responseText?: string;
  }) => void;
  onClose: () => void;
}> = ({ onSave, onClose }) => {
  const [reviewerId, setReviewerId] = useState<number>(0);
  const [revieweeId, setRevieweeId] = useState<number>(0);
  const [reviewerType, setReviewerType] = useState<'client' | 'worker'>('client');
  const [revieweeType, setRevieweeType] = useState<'client' | 'worker'>('worker');
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [responseText, setResponseText] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      reviewerId,
      revieweeId,
      reviewerType,
      revieweeType,
      rating,
      reviewText,
      responseText: responseText.trim() || undefined
    });
    onClose();
  };
  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Reviewer ID</label>
        <input
          type="number"
          value={reviewerId}
          onChange={(e) => setReviewerId(parseInt(e.target.value))}
          className="form-control"
          required
          min="1"
        />
      </div>
      <div className="form-group">
        <label>Reviewee ID</label>
        <input
          type="number"
          value={revieweeId}
          onChange={(e) => setRevieweeId(parseInt(e.target.value))}
          className="form-control"
          required
          min="1"
        />
      </div>
      <div className="form-group">
        <label>Reviewer Type</label>
        <select
          value={reviewerType}
          onChange={(e) => setReviewerType(e.target.value as 'client' | 'worker')}
          className="form-control"
          required
        >
          <option value="client">Client</option>
          <option value="worker">Worker</option>
        </select>
      </div>
      <div className="form-group">
        <label>Reviewee Type</label>
        <select
          value={revieweeType}
          onChange={(e) => setRevieweeType(e.target.value as 'client' | 'worker')}
          className="form-control"
          required
        >
          <option value="client">Client</option>
          <option value="worker">Worker</option>
        </select>
      </div>
      <div className="form-group">
        <label>Rating</label>
        <select
          value={rating}
          onChange={(e) => setRating(parseInt(e.target.value))}
          className="form-control"
          required
        >
          <option value={1}>1 Star</option>
          <option value={2}>2 Stars</option>
          <option value={3}>3 Stars</option>
          <option value={4}>4 Stars</option>
          <option value={5}>5 Stars</option>
        </select>
      </div>
      <div className="form-group">
        <label>Review Text</label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          className="form-control"
          rows={4}
          required
          placeholder="Enter the review text..."
        />
      </div>
      <div className="form-group">
        <label>Response Text (Optional)</label>
        <textarea
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
          className="form-control"
          rows={2}
          placeholder="Enter response text (optional)..."
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn-primary">Add Review</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

const ReviewsModeration: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedReviews, setSelectedReviews] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'view' | 'edit' | 'respond' | 'flag' | 'add'>('view');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => { });
  const [confirmMessage, setConfirmMessage] = useState('');

  // Confirmation modal helper
  const showConfirmation = async (message: string, action: () => void) => {
    const result = await Swal.fire({
      title: 'Confirm Action',
      text: message,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (result.isConfirmed) {
      action();
    }
  };

  const handleConfirm = () => {
    confirmAction();
    setShowConfirmModal(false);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  // API service functions
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('skillseek_admin_token');

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        status: statusFilter,
        rating: ratingFilter,
        type: typeFilter
      });
      const response = await fetch(`${API_BASE_URL}/admin/reviews?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const data = await response.json();

      if (data.success) {
        setReviews(data.reviews || []);
        // Handle case when pagination might be undefined
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages || 1);
          setTotalItems(data.pagination.totalItems || 0);
        } else {
          // Set default values if pagination is missing
          setTotalPages(1);
          setTotalItems(data.reviews ? data.reviews.length : 0);
          console.warn('Pagination data is missing in the API response');
        }
      } else {
        throw new Error('Failed to fetch reviews');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      alert('Error fetching reviews');
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId: number, newStatus: 'active' | 'hidden' | 'flagged', reason?: string) => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');

      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, reason })
      });

      if (!response.ok) {
        throw new Error('Failed to update review status');
      }

      const data = await response.json();

      if (data.success) {
        // Update local state
        setReviews(prev => prev.map(review =>
          review.id === reviewId
            ? { ...review, status: newStatus, updatedAt: new Date().toISOString() }
            : review
        ));
        Swal.fire({
          title: 'Success!',
          text: data.message,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
      } else {
        throw new Error(data.message || 'Failed to update review status');
      }
    } catch (error) {
      console.error('Error updating review status:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error updating review status',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const editReview = async (reviewId: number, reviewText: string, rating: number, moderationReason?: string) => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');

      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/edit`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reviewText, rating, moderationReason })
      });

      if (!response.ok) {
        throw new Error('Failed to edit review');
      }

      const data = await response.json();

      if (data.success) {
        // Refresh reviews
        await fetchReviews();
        alert(data.message);
      } else {
        throw new Error(data.message || 'Failed to edit review');
      }
    } catch (error) {
      console.error('Error editing review:', error);
      alert('Error editing review');
    }
  };

  const addReviewResponse = async (reviewId: number, responseText: string) => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');

      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/response`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ responseText })
      });

      if (!response.ok) {
        throw new Error('Failed to add response');
      }

      const data = await response.json();

      if (data.success) {
        // Update local state
        setReviews(prev => prev.map(review =>
          review.id === reviewId
            ? { ...review, responseText, updatedAt: new Date().toISOString() }
            : review
        ));
        alert(data.message);
      } else {
        throw new Error(data.message || 'Failed to add response');
      }
    } catch (error) {
      console.error('Error adding response:', error);
      alert('Error adding response');
    }
  };

  const deleteReview = async (reviewId: number) => {
    const performDelete = async () => {
      try {
        const token = localStorage.getItem('skillseek_admin_token');

        const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete review');
        }

        const data = await response.json();

        if (data.success) {
          // Remove from local state
          setReviews(prev => prev.filter(review => review.id !== reviewId));
          Swal.fire({
            title: 'Success!',
            text: data.message,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745'
          });
        } else {
          throw new Error(data.message || 'Failed to delete review');
        }
      } catch (error) {
        console.error('Error deleting review:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Error deleting review',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    };

    showConfirmation('Are you sure you want to delete this review?', performDelete);
  };

  const filterReviews = () => {
    return reviews.filter(review => {
      const searchQuery = searchTerm.toLowerCase();
      const reviewText = review.reviewText.toLowerCase();
      const responseText = review.responseText?.toLowerCase() || '';
      const firstName = review.reviewerName.toLowerCase();
      const lastName = review.revieweeName.toLowerCase();
      return (
        reviewText.includes(searchQuery) ||
        responseText.includes(searchQuery) ||
        firstName.includes(searchQuery) ||
        lastName.includes(searchQuery)
      );
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };


  const bulkUpdateReviews = async (reviewIds: number[], action: 'approve' | 'hide' | 'flag' | 'delete', reason?: string) => {
    const performBulkUpdate = async () => {
      try {
        const token = localStorage.getItem('skillseek_admin_token');

        const response = await fetch(`${API_BASE_URL}/admin/reviews/bulk`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reviewIds, action, reason })
        });

        if (!response.ok) {
          throw new Error('Failed to update reviews');
        }

        const data = await response.json();

        if (data.success) {
          // Refresh reviews
          await fetchReviews();
          setSelectedReviews([]);
          Swal.fire({
            title: 'Success!',
            text: data.message,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745'
          });
        } else {
          throw new Error(data.message || 'Failed to update reviews');
        }
      } catch (error) {
        console.error('Error updating reviews:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Error updating reviews',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    };

    showConfirmation(`Are you sure you want to ${action} ${reviewIds.length} reviews?`, performBulkUpdate);
  };

  const addReview = async (reviewData: {
    reviewerId: number;
    revieweeId: number;
    reviewerType: 'client' | 'worker';
    revieweeType: 'client' | 'worker';
    rating: number;
    reviewText: string;
    responseText?: string;
  }) => {
    try {
      const token = localStorage.getItem('skillseek_admin_token');

      const response = await fetch(`${API_BASE_URL}/admin/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reviewData)
      });

      if (!response.ok) {
        throw new Error('Failed to add review');
      }

      const data = await response.json();

      if (data.success) {
        // Refresh reviews
        await fetchReviews();
        Swal.fire({
          title: 'Success!',
          text: data.message,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
      } else {
        throw new Error(data.message || 'Failed to add review');
      }
    } catch (error) {
      console.error('Error adding review:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error adding review',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Fetch reviews on component mount and when filters change
  useEffect(() => {
    fetchReviews();
  }, [currentPage, statusFilter, ratingFilter, typeFilter]);

  const handleSelectReview = (reviewId: number) => {
    setSelectedReviews(prev =>
      prev.includes(reviewId)
        ? prev.filter(id => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReviews.length === reviews.length) {
      setSelectedReviews([]);
    } else {
      setSelectedReviews(reviews.map(r => r.id));
    }
  };

  const handleAction = (review: Review | null, action: 'view' | 'edit' | 'respond' | 'flag' | 'add') => {
    setSelectedReview(review);
    setModalType(action);
    setShowModal(true);
  };

  const handleStatusUpdate = (reviewId: number, newStatus: 'active' | 'hidden' | 'flagged') => {
    updateReviewStatus(reviewId, newStatus);
  };

  const exportReviews = () => {
    const csvContent = [
      ['Review ID', 'Reviewer', 'Reviewee', 'Rating', 'Status', 'Date', 'Review Text'].join(','),
      ...reviews.map(review => [
        review.id,
        review.reviewerName,
        review.revieweeName,
        review.rating,
        review.status,
        new Date(review.createdAt).toLocaleDateString(),
        `"${review.reviewText.replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reviews.csv';
    a.click();
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'badge-success',
      hidden: 'badge-warning',
      reported: 'badge-danger',
      flagged: 'badge-danger'
    };
    return `badge ${colors[status as keyof typeof colors] || 'badge-secondary'}`;
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={14}
        style={{
          color: i < rating ? 'var(--accent-orange)' : 'var(--text-dark-gray)',
          fill: i < rating ? 'var(--accent-orange)' : 'none'
        }}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Reviews & Ratings Moderation" subtitle="Monitor and moderate platform reviews">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  // Calculate statistics
  const stats = {
    total: totalItems,
    active: reviews.filter(r => r.status === 'active').length,
    reported: reviews.filter(r => r.status === 'reported' || r.status === 'flagged').length,
    avgRating: reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0,
    verified: reviews.filter(r => r.isVerified).length
  };

  return (
    <AdminLayout title="Reviews & Ratings Moderation" subtitle="Monitor and moderate platform reviews">
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
              {/* Search */}
              <div style={{ position: 'relative', minWidth: '300px' }}>
                < input
                  type="text"
                  placeholder="Search reviews..."
                  className="form-control"
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{ paddingLeft: '40px' }}
                />
                <Search size={18} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dark-gray)'
                }} />
              </div>

              {/* Filters */}
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
                <option value="reported">Reported</option>
                <option value="flagged">Flagged</option>
              </select>

              <select
                className="form-control"
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>

              <select
                className="form-control"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ minWidth: '140px' }}
              >
                <option value="all">All Types</option>
                <option value="client">Client Reviews</option>
                <option value="worker">Worker Reviews</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-primary" onClick={() => handleAction(null, 'add')}>
                <MessageSquare size={18} />
                Add Review
              </button>
              <button className="btn-secondary" onClick={exportReviews}>
                <Download size={18} />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stat-cards" style={{ marginBottom: '25px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <MessageSquare size={24} />
            </div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Active Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Flag size={24} />
            </div>
            <div className="stat-value">{stats.reported}</div>
            <div className="stat-label">Needs Attention</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Star size={24} />
            </div>
            <div className="stat-value">{stats.avgRating.toFixed(1)}</div>
            <div className="stat-label">Average Rating</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-value">{stats.verified}</div>
            <div className="stat-label">Verified Reviews</div>
          </div>
        </div>

        {/* Reviews Table */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Reviews & Ratings</div>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Showing {reviews.length} of {totalItems} reviews
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedReviews.length === reviews.length && reviews.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Review Details</th>
                  <th>Participants</th>
                  <th>Rating & Content</th>

                  <th>Status</th>
                  <th>Engagement</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filterReviews().map(review => (
                  <tr key={review.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedReviews.includes(review.id)}
                        onChange={() => handleSelectReview(review.id)}
                      />
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                          Review #{review.id}
                        </div>
                        {review.reportCount > 0 && (
                          <div style={{
                            fontSize: '10px',
                            color: 'var(--accent-red)',
                            marginTop: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Flag size={10} />
                            {review.reportCount} reports
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>From:</strong> {review.reviewerName}
                          <span className={`badge badge-${review.reviewerType === 'client' ? 'info' : 'success'}`}
                            style={{ marginLeft: '8px', fontSize: '10px' }}>
                            {review.reviewerType}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          <strong>About:</strong> {review.revieweeName}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            {getRatingStars(review.rating)}
                          </div>
                          <span style={{ fontWeight: '500' }}>{review.rating}/5</span>
                          {review.isVerified && (
                            <CheckCircle size={12} style={{ color: 'var(--accent-green)' }} />
                          )}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--text-light-gray)',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {review.reviewText}
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className={getStatusBadge(review.status)}>
                        {review.status}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div style={{
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginBottom: '2px'
                        }}>
                          <ThumbsUp size={12} />
                          {review.helpfulCount}
                        </div>
                        {review.responseText && (
                          <div style={{ fontSize: '10px', color: 'var(--accent-blue)' }}>
                            Has response
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {formatDate(review.createdAt)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(review, 'view')}
                          title="View Full Review"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(review, 'edit')}
                          title="Edit Review"
                        >
                          <Edit size={14} />
                        </button>
                        {review.status === 'active' ? (
                          <button
                            className="btn-warning"
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => handleStatusUpdate(review.id, 'hidden')}
                            title="Hide Review"
                          >
                            <XCircle size={14} />
                          </button>
                        ) : (
                          <button
                            className="btn-success"
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => handleStatusUpdate(review.id, 'active')}
                            title="Show Review"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        <button
                          className={review.status === 'flagged' ? 'btn-primary' : 'btn-danger'}
                          style={{
                            padding: '6px',
                            minWidth: 'auto',
                            opacity: review.status === 'flagged' ? 1 : 0.6,
                            background: review.status === 'flagged' ? '#dc3545' : undefined,
                            border: review.status === 'flagged' ? '2px solid #fff' : undefined,
                            boxShadow: review.status === 'flagged' ? '0 0 0 2px #dc3545' : undefined,
                          }}
                          onClick={() => review.status === 'flagged'
                            ? handleStatusUpdate(review.id, 'active')  // unflag
                            : handleAction(review, 'flag')              // flag
                          }
                          title={review.status === 'flagged' ? 'Unflag Review' : 'Flag Review'}
                        >
                          <Flag size={14} fill={review.status === 'flagged' ? 'white' : 'none'} />
                        </button>
                        <button
                          className="btn-danger"
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => deleteReview(review.id)}
                          title="Delete Review"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            padding: '20px 0'
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button
                className="btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedReviews.length > 0 && (
          <div className="admin-card" style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            minWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{selectedReviews.length}</strong> reviews selected
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn-success"
                  onClick={() => bulkUpdateReviews(selectedReviews, 'approve', 'Bulk approved by admin')}
                >
                  <CheckCircle size={18} />
                  Approve All
                </button>
                <button
                  className="btn-warning"
                  onClick={() => bulkUpdateReviews(selectedReviews, 'hide', 'Bulk hidden by admin')}
                >
                  <XCircle size={18} />
                  Hide All
                </button>
                <button
                  className="btn-danger"
                  onClick={() => bulkUpdateReviews(selectedReviews, 'flag', 'Bulk flagged by admin')}
                >
                  <Flag size={18} />
                  Flag All
                </button>
                <button
                  className="btn-danger"
                  onClick={() => bulkUpdateReviews(selectedReviews, 'delete', 'Bulk deleted by admin')}
                >
                  <Trash2 size={18} />
                  Delete All
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setSelectedReviews([])}
                >
                  <XCircle size={18} />
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Action Modals */}
        {showModal && (modalType === 'add' || selectedReview) && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  {modalType === 'view' && 'View Review'}
                  {modalType === 'edit' && 'Edit Review'}
                  {modalType === 'respond' && 'Add Response'}
                  {modalType === 'flag' && 'Flag Review'}
                  {modalType === 'add' && 'Add New Review'}
                </h3>
                <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <div className="modal-body">
                {modalType === 'view' && selectedReview && (
                  <div>
                    <p><strong>Reviewer:</strong> {selectedReview.reviewerName}</p>
                    <p><strong>Reviewee:</strong> {selectedReview.revieweeName}</p>
                    <p><strong>Rating:</strong> {selectedReview.rating}/5</p>
                    <p><strong>Review:</strong> {selectedReview.reviewText}</p>
                    {selectedReview.responseText && (
                      <p><strong>Response:</strong> {selectedReview.responseText}</p>
                    )}
                    <p><strong>Status:</strong> {selectedReview.status}</p>
                    <p><strong>Date:</strong> {formatDate(selectedReview.createdAt)}</p>
                  </div>
                )}
                {modalType === 'edit' && selectedReview && (
                  <EditReviewForm
                    review={selectedReview}
                    onSave={editReview}
                    onClose={() => setShowModal(false)}
                  />
                )}
                {modalType === 'respond' && selectedReview && (
                  <ResponseForm
                    review={selectedReview}
                    onSave={addReviewResponse}
                    onClose={() => setShowModal(false)}
                  />
                )}
                {modalType === 'flag' && selectedReview && (
                  <FlagReviewForm
                    review={selectedReview}
                    onSave={(reviewId, reason) => updateReviewStatus(reviewId, 'flagged', reason)}
                    onClose={() => setShowModal(false)}
                  />
                )}
                {modalType === 'add' && (
                  <AddReviewForm
                    onSave={addReview}
                    onClose={() => setShowModal(false)}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="modal-overlay" onClick={handleCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirm Action</h3>
                <button className="modal-close" onClick={handleCancel}>×</button>
              </div>
              <div className="modal-body">
                <p>{confirmMessage}</p>
              </div>
              <div className="modal-footer" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #eee'
              }}>
                <button className="btn-danger" onClick={handleConfirm}>
                  Confirm
                </button>
                <button className="btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Helper components for modal forms
const EditReviewForm: React.FC<{
  review: Review;
  onSave: (reviewId: number, reviewText: string, rating: number, moderationReason?: string) => void;
  onClose: () => void;
}> = ({ review, onSave, onClose }) => {
  const [reviewText, setReviewText] = useState(review.reviewText);
  const [rating, setRating] = useState(review.rating);
  const [moderationReason, setModerationReason] = useState(review.moderationReason || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(review.id, reviewText, rating, moderationReason);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Review Text</label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          className="form-control"
          rows={4}
          required
        />
      </div>
      <div className="form-group">
        <label>Rating</label>
        <select
          value={rating}
          onChange={(e) => setRating(parseInt(e.target.value))}
          className="form-control"
          required
        >
          <option value={1}>1 Star</option>
          <option value={2}>2 Stars</option>
          <option value={3}>3 Stars</option>
          <option value={4}>4 Stars</option>
          <option value={5}>5 Stars</option>
        </select>
      </div>
      <div className="form-group">
        <label>Moderation Reason</label>
        <textarea
          value={moderationReason}
          onChange={(e) => setModerationReason(e.target.value)}
          className="form-control"
          rows={2}
          placeholder="Reason for editing this review..."
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn-primary">Save Changes</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

const ResponseForm: React.FC<{
  review: Review;
  onSave: (reviewId: number, responseText: string) => void;
  onClose: () => void;
}> = ({ review, onSave, onClose }) => {
  const [responseText, setResponseText] = useState(review.responseText || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(review.id, responseText);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Response Text</label>
        <textarea
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
          className="form-control"
          rows={4}
          placeholder="Enter your response to this review..."
          required
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn-primary">Add Response</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

const FlagReviewForm: React.FC<{
  review: Review;
  onSave: (reviewId: number, reason: string) => void;
  onClose: () => void;
}> = ({ review, onSave, onClose }) => {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(review.id, reason);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Flag Reason</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="form-control"
          rows={3}
          placeholder="Enter reason for flagging this review..."
          required
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn-danger">Flag Review</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

export default ReviewsModeration; 